import React, { useState, useEffect } from 'react';
import { isPushSupported, enablePush, getCurrentSubscription } from '../lib/push';

const DISMISS_KEY = 'dd_notify_banner_dismissed';

/* Gentle prompt on the dashboard inviting users to turn on phone reminders. */
const NotifyBanner = () => {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    if (localStorage.getItem(DISMISS_KEY) === '1') return;
    if (typeof Notification !== 'undefined' && Notification.permission === 'denied') return;

    (async () => {
      try {
        const sub = await getCurrentSubscription();
        if (!sub) setVisible(true);
      } catch (e) {
        setVisible(true);
      }
    })();
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    try {
      await enablePush();
      setDone(true);
      setTimeout(() => setVisible(false), 1800);
    } catch (err) {
      // If blocked or unsupported, just hide quietly
      setVisible(false);
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="notify-banner"
      className="mb-6 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-4 text-white shadow-lg flex items-center justify-between gap-3"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-2xl">🔔</span>
        <div className="min-w-0">
          <p className="font-semibold text-sm">
            {done ? "You're all set!" : 'Never miss a tiny win'}
          </p>
          <p className="text-emerald-100 text-xs truncate">
            {done ? 'Reminders will arrive at your set time.' : 'Turn on phone reminders — no email needed.'}
          </p>
        </div>
      </div>
      {!done && (
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            data-testid="notify-banner-enable"
            onClick={handleEnable}
            disabled={busy}
            className="bg-white text-emerald-700 font-semibold text-sm px-4 py-2 rounded-lg hover:bg-emerald-50 transition disabled:opacity-60"
          >
            {busy ? '...' : 'Turn on'}
          </button>
          <button
            data-testid="notify-banner-dismiss"
            onClick={handleDismiss}
            className="text-emerald-100 hover:text-white text-xl leading-none px-1"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default NotifyBanner;
